import { z } from 'zod';

export const generateZodSchema = (t, isDuplicate, property, order) =>
  z
    .object({
      key: z
        .string()
        .min(1, t('Form.FormErrorNotBlank'))
        .regex(
          /^[a-zA-Z0-9_]*[a-zA-Z_]+[a-zA-Z0-9_]*$/,
          t('PropertyForm.Errors.Key'),
        )
        .refine((value) => {
          if (isDuplicate && property?.key === value) return false;
          return (
            order.filter((keys) => keys !== property?.key).indexOf(value) < 0
          );
        }, t('PropertyForm.Errors.UniqueKey')),
      config: z.object({
        inputType: z.string(),
        label: z.string().min(1, t('Form.FormErrorNotBlank')),
        items: z.object({}).optional(),
        useOptionsWithLabels: z.boolean().optional(),
        options: z.array(z.string()).optional(),
        optionsWithLabels: z
          .array(z.object({ value: z.string(), label: z.string() }))
          .optional(),
        multiple: z.boolean().optional(),
        validation: z
          .object({
            relationContenttype: z.string().optional(),
            relationMultiple: z.boolean().optional(),
          })
          .optional(),
      }),
      schema: z.object({
        default: z.any().optional(),
        type: z.string(),
        pattern: z
          .string()
          .optional()
          .refine((value) => {
            try {
              new RegExp(value);
              return true;
            } catch {
              return false;
            }
          }, t('PropertyForm.Errors.Pattern')),
      }),
    })
    .superRefine((object, context) => {
      if (['select', 'radio'].indexOf(object.config.inputType) < 0) return;

      const optionKeys = object.config.useOptionsWithLabels
        ? (object.config.optionsWithLabels || []).map((option) => option.value)
        : object.config.options;

      const path = object.config.useOptionsWithLabels
        ? ['config', 'optionsWithLabels']
        : ['config', 'options'];

      if (!optionKeys?.length) {
        context.addIssue({
          expected: 'not null',
          code: 'defined',
          path,
          message: t('PropertyForm.Errors.OptionsLength'),
        });
      } else if (optionKeys.length !== new Set(optionKeys).size) {
        context.addIssue({
          expected: 'no duplications',
          code: 'no_duplications',
          path,
          message: t('PropertyForm.Errors.OptionsDuplications'),
        });
      }
    })
    .superRefine((object, context) => {
      const defaultValue = object.schema.default;
      const schemaType = object.schema.type;
      if (!defaultValue || !schemaType) return;

      const inputType = object.config?.inputType;

      let validator;

      if (inputType === 'email') {
        validator = z.union([
          z.literal(''),
          z.string().email({ message: t('ContentForm.Errors.Email') }),
        ]);
      } else if (['select', 'radio'].indexOf(inputType) > -1) {
        validator = getOptionsValidator(object, t);
      } else if (inputType === 'geo') {
        validator = getGeoValidator(t);
      } else if (object.config?.validation) {
        validator = getContentTypeValidator(object, t);
        if (!object.config.validation.relationMultiple) {
          validator = validator.max(1, t('ContentForm.Errors.MaxLength'));
        }
      } else if (object.schema.pattern) {
        try {
          const errorMessage =
            inputType === 'dateTime'
              ? t('ContentForm.Errors.Pattern', {
                  pattern: object.schema.pattern,
                })
              : t('PropertyForm.Errors.Default');

          const stringValidator = z.union([
            z.literal(''),
            z.string().regex(new RegExp(object.schema.pattern), errorMessage),
          ]);

          validator =
            schemaType === 'array' ? z.array(stringValidator) : stringValidator;
        } catch {
          /** Pattern is wrong regexp, nothing to do */
        }
      }

      if (validator) {
        const result = validator.safeParse(defaultValue);
        if (!result.success) {
          context.addIssue(
            ...result.error.issues.map((issue) => ({
              ...issue,
              path: ['schema', 'default'],
            })),
          );
        }
      }
    });

export const validateListField = (t, order, formApi) => {
  if (formApi.getFieldValue('config.inputType') !== 'object') return;
  if (!order?.length) {
    return t('ContentTypeForm.Errors.OneProperty');
  }
};

const getOptionsValidator = (object, t) => {
  let optionKeys = object.config?.options || [];
  if (object.config?.useOptionsWithLabels)
    optionKeys = object.config?.optionsWithLabels?.map(
      (option) => option.value,
    );

  if (object.config.multiple) {
    return z
      .array(z.string())
      .refine(
        (value) =>
          value.length === 0 ||
          !(value || []).find((option) => !optionKeys.includes(option)),
        {
          message: t('ContentForm.Errors.OptionNotFound'),
        },
      );
  }

  return z.string().refine((value) => !value || optionKeys.includes(value), {
    message: t('ContentForm.Errors.OptionNotFound'),
  });
};

const getGeoValidator = (t) => {
  const latError = t('ContentForm.Errors.Coordinates', {
    coordinate: t('ContentForm.Latitude'),
    max: 90,
    min: -90,
  });
  const lonError = t('ContentForm.Errors.Coordinates', {
    coordinate: t('ContentForm.Longitude'),
    max: 180,
    min: -180,
  });

  return z.object({
    lat: z
      .number()
      .min(-90, { message: latError })
      .max(90, { message: latError })
      .optional(),
    lon: z
      .number()
      .min(-180, { message: lonError })
      .max(180, { message: lonError })
      .optional(),
  });
};

const getContentTypeValidator = (object, t) => {
  if (object.config.validation.relationContenttype) {
    return z.array(
      z.object({
        dataUrl: z
          .string()
          .regex(
            new RegExp(`^.*${object.config.validation.relationContenttype}.*$`),
            t('ContentForm.Errors.DataUrl'),
          ),
      }),
    );
  }

  return z.array(
    z.object({
      dataUrl: z.string(),
    }),
  );
};
